import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  useGetWaitlistQuery,
  useUpdateWaitlistMutation,
  useDeleteWaitlistMutation,
  useAddWaitlistMutation,
} from "apis/waitlist";
import Modal from "../components/ui/Modal";
import WaitlistEdit from "../components/waitlist/WaitlistEdit";
import Button from "components/ui/Button";
import moment from "moment";
import Table from "components/ui/Table";

const Waitlist = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const pageSize = 10;
  const [isAddOpen, setIsAddOpen] = useState(false);

  const {
    data,
    isPending: isWaitlistPending,
  } = useGetWaitlistQuery();

  const { mutate: updateWaitlist } = useUpdateWaitlistMutation();
  const { mutate: deleteWaitlist } = useDeleteWaitlistMutation();
  const { mutate: addWaitlist } = useAddWaitlistMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        isFilter: true,
      },
      {
        accessorKey: "email",
        header: "Email",
        isFilter: true,
      },
      {
        accessorKey: "phone",
        header: "Phone",
        isFilter: true,
      },
      {
        accessorKey: "region",
        header: "Region",
        cell: (row) => row.region || "N/A",
        isFilter: true,
      },
      {
        accessorKey: "details",
        header: "Details",
        isFilter: true,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        isFilter: false,
        cell: (row) =>
          row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-",
      },
      {
        accessorKey: "isContacted",
        header: "Contacted",
        isFilter: false,
        cell: (row) => {
          const { isContacted, contactedAt } = row;

          return (
            <div className="flex flex-col gap-1">
              <span
                className={`px-2 py-1 rounded text-xs inline-block w-fit ${
                  isContacted
                    ? "bg-green-100 text-green-600"
                    : "bg-lightergray text-darkergray"
                }`}
              >
                {isContacted ? "Yes" : "No"}
              </span>

              {isContacted && contactedAt && (
                <span className="text-[10px] text-darkgray">
                  {moment(contactedAt).format("DD MMM YYYY, hh:mm A")}
                </span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Entry"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setDeleteConfirmId(row._id);
                setIsDeleteConfirmOpen(true);
              }}
              className="delete-icon"
              title="Delete Entry"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  if (isWaitlistPending || !data) {
    return <PageLoader />;
  }

  return (
    <>
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Waitlist Entry"
        className="max-w-2xl"
      >
        <WaitlistEdit
          initialName={""}
          initialEmail={""}
          initialPhone={""}
          initialRegion={""}
          initialDetails={""}
          initialIsContacted={false}
          onSubmit={(formData) => {
            addWaitlist(formData);
            setIsAddOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Waitlist Entry"
        className="max-w-2xl"
      >
        <WaitlistEdit
          initialName={data.find((item) => item._id === selectedId)?.name}
          initialEmail={data.find((item) => item._id === selectedId)?.email}
          initialPhone={data.find((item) => item._id === selectedId)?.phone}
          initialRegion={data.find((item) => item._id === selectedId)?.region}
          initialDetails={data.find((item) => item._id === selectedId)?.details}
          initialIsContacted={
            data.find((item) => item._id === selectedId)?.isContacted || false
          }
          onSubmit={(formData) => {
            updateWaitlist({ id: selectedId, ...formData });
            setIsEditOpen(false);
          }}
        />
      </Modal>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="Confirm Delete"
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-darkergray">
            Are you sure you want to delete this entry?
          </p>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="gray-button"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                deleteWaitlist(deleteConfirmId);
                setIsDeleteConfirmOpen(false);
              }}
              className="black-button"
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <div className="pagination-info flex justify-between align-center items-center mb-4">
          <h3 className="text-lg font-semibold">Waitlist</h3>
          <div>
            <Button onClick={() => setIsAddOpen(true)} className="black-button">
              Add Entry
            </Button>
          </div>
        </div>
        <Table
          title="Waitlist"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
        />
      </div>
    </>
  );
};

export default Waitlist;
